<template>
  <section v-if="hasRelated" class="px-5 md:px-10 pb-10 space-y-10">
    <div v-if="variant.relatedProductVariantsCount">
      <h2 class="mb-5 text-xl font-bold text-primary-dark">{{ $t('text-related-products') }}</h2>
      <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <RenderProductCard v-for="item in productItems" :key="item.id" :variant="item" />
      </div>
      <div v-if="productPagination.page < productPagination.pageCount" class="flex justify-center mt-6">
        <Button :loading="loadingProducts" :on-click="loadMoreProducts">{{ $t('text-load-more') }}</Button>
      </div>
    </div>

    <div v-if="variant.relatedInspirationPostsCount">
      <h2 class="mb-5 text-xl font-bold text-primary-dark">{{ $t('text-related-inspiration') }}</h2>
      <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <RenderContentCard v-for="item in inspirationItems" :key="item.id" :variant="item" :type="CONTENT_TYPE.INSPIRATION" />
      </div>
      <div v-if="inspirationPagination.page < inspirationPagination.pageCount" class="flex justify-center mt-6">
        <Button :loading="loadingInspirations" :on-click="loadMoreInspirations">{{ $t('text-load-more') }}</Button>
      </div>
    </div>

    <div v-if="variant.relatedNewsPostsCount">
      <h2 class="mb-5 text-xl font-bold text-primary-dark">{{ $t('text-related-news') }}</h2>
      <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <RenderContentCard v-for="item in newsItems" :key="item.id" :variant="item" :type="CONTENT_TYPE.NEWS" />
      </div>
      <div v-if="newsPagination.page < newsPagination.pageCount" class="flex justify-center mt-6">
        <Button :loading="loadingNews" :on-click="loadMoreNews">{{ $t('text-load-more') }}</Button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { API_ENDPOINTS } from '@utils/api/endpoints';
import { CONTENT_TYPE } from '@utils/constants';
import RenderProductCard from '@components/product/render-product-card';
import RenderContentCard from '@components/content/render-content-card';
import Button from '@components/ui/button';

const props = defineProps({
  variant: {
    type: Object,
    required: true
  }
});

const routeLang = useRouteLang();
const productItems = ref([]);
const inspirationItems = ref([]);
const newsItems = ref([]);
const productPagination = ref({ page: 0, pageCount: 0 });
const inspirationPagination = ref({ page: 0, pageCount: 0 });
const newsPagination = ref({ page: 0, pageCount: 0 });
const loadingProducts = ref(false);
const loadingInspirations = ref(false);
const loadingNews = ref(false);
const hasRelated = computed(() => props.variant.relatedProductVariantsCount
  || props.variant.relatedInspirationPostsCount
  || props.variant.relatedNewsPostsCount);

const loadProducts = async (page = 1) => {
  loadingProducts.value = true;
  try {
    const data = await $apiFetch(API_ENDPOINTS.CATALOGUE_POST_ID_PRODUCT_VARIANT, {
      method: 'GET',
      path: { id: props.variant.id },
      params: { page, limit: 6, locale: routeLang.locale.value }
    });
    productItems.value.push(...(data?.items || []));
    productPagination.value = data?.pagination || productPagination.value;
  } catch (error) {
    sentryCaptureEvent({
      message: 'catalogue-related-items.loadProducts',
      level: 'error',
      extra: { error }
    });
  } finally {
    loadingProducts.value = false;
  }
};

const loadInspirations = async (page = 1) => {
  loadingInspirations.value = true;
  try {
    const data = await $apiFetch(API_ENDPOINTS.CATALOGUE_POST_ID_CONTENT_POST, {
      method: 'GET',
      path: { id: props.variant.id },
      params: { type: CONTENT_TYPE.INSPIRATION, page, limit: 6, locale: routeLang.locale.value }
    });
    inspirationItems.value.push(...(data?.items || []));
    inspirationPagination.value = data?.pagination || inspirationPagination.value;
  } catch (error) {
    sentryCaptureEvent({
      message: 'catalogue-related-items.loadInspirations',
      level: 'error',
      extra: { error }
    });
  } finally {
    loadingInspirations.value = false;
  }
};

const loadNews = async (page = 1) => {
  loadingNews.value = true;
  try {
    const data = await $apiFetch(API_ENDPOINTS.CATALOGUE_POST_ID_CONTENT_POST, {
      method: 'GET',
      path: { id: props.variant.id },
      params: { type: CONTENT_TYPE.NEWS, page, limit: 6, locale: routeLang.locale.value }
    });
    newsItems.value.push(...(data?.items || []));
    newsPagination.value = data?.pagination || newsPagination.value;
  } catch (error) {
    sentryCaptureEvent({
      message: 'catalogue-related-items.loadNews',
      level: 'error',
      extra: { error }
    });
  } finally {
    loadingNews.value = false;
  }
};

const loadMoreProducts = () => loadProducts(productPagination.value.page + 1);
const loadMoreInspirations = () => loadInspirations(inspirationPagination.value.page + 1);
const loadMoreNews = () => loadNews(newsPagination.value.page + 1);

onMounted(async () => {
  await Promise.all([
    props.variant.relatedProductVariantsCount ? loadProducts() : Promise.resolve(),
    props.variant.relatedInspirationPostsCount ? loadInspirations() : Promise.resolve(),
    props.variant.relatedNewsPostsCount ? loadNews() : Promise.resolve()
  ]);
});
</script>
